<template>
  <div class="p-1 advanced-table-wrapper">
    <div v-if="isLoading" class="loading">
      <img src="@/assets/images/loading.gif" alt="Loading" />
    </div>
    <div v-else-if="isEmptyTable" class="empty">
      <img src="@/assets/images/bulk-empty@2x.png" alt="Empty" />
      <div class="empty-text">
        <span class="empty-text-title">{{ emptyText.type }}</span>
        <span class="empty-text-description">{{ emptyText.description }}</span>
      </div>
    </div>
    <template v-else>
      <div class="table-wrapper">
        <table class="advanced-table">
          <thead>
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :style="{ width: `${header.getSize()}px` }"
                :colSpan="header.colSpan"
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in table.getRowModel().rows" :key="row.id" :class="table.options.meta?.getRowClasses(row)">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                <template v-if="cell.column.id === 'failedCount'">
                  <span v-if="cell.getValue() > 0" class="error-column-item">
                    <span>
                      <img
                        width="16px"
                        height="16px"
                        src="@/assets/images/icons/alert-circle.svg"
                        alt="Error Icon"
                      />
                      {{ cell.getValue() }} Hata
                    </span>
                    <button class="report-button"
                      @click="downloadReport(cell.row.original)">
                      Raporu İndir
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M17.7151 9.29289C18.1056 9.68342 18.1056 10.3166 17.7151 10.7071L12.757 15.667L12.6563 15.7632C12.589 15.8195 12.5147 15.8668 12.4356 15.904L12.3449 15.9416L12.2509 15.9704C11.9332 16.0515 11.5748 15.981 11.3009 15.7071L6.30091 10.7071L6.22161 10.6202C5.44337 9.68075 6.80088 8.37865 7.71512 9.29289L11.008 12.585V5L11.0147 4.88338C11.0725 4.38604 11.4952 4 12.008 4L12.1246 4.00673C12.622 4.06449 13.008 4.48716 13.008 5V12.586L16.3009 9.29289L16.3951 9.2097C16.7874 8.90468 17.3546 8.93241 17.7151 9.29289ZM6 17.992C5.44772 17.992 5 18.4397 5 18.992C5 19.5443 5.44772 19.992 6 19.992H18C18.5523 19.992 19 19.5443 19 18.992C19 18.4397 18.5523 17.992 18 17.992H6Z" fill="currentColor"/>
                      </svg>
                    </button>
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-else-if="cell.column.id === 'id'">
                  <AppButton
                    v-if="['DOWNLOADED', 'WAITING_FOR_DOWNLOAD'].includes(cell.row.original.status)"
                    style="max-width: fit-content; height: 24px !important;"
                    secondary
                    @click="handleTemplateDownload(cell.getValue(), cell.row.original.templateFile, cell.row.original.status)" 
                    xsm
                    title="İndir"
                  >
                    <template #prependIcon>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.5 13.4941C13.9142 13.4941 14.25 13.8299 14.25 14.2441C14.2499 14.6583 13.9142 14.9941 13.5 14.9941H4.5C4.08584 14.9941 3.75008 14.6583 3.75 14.2441C3.75 13.8299 4.08579 13.4941 4.5 13.4941H13.5ZM9.09375 3.00488C9.46663 3.04832 9.75586 3.36546 9.75586 3.75V9.43945L12.2256 6.96973L12.2959 6.90723C12.5901 6.67848 13.0158 6.69943 13.2861 6.96973C13.579 7.26262 13.579 7.73738 13.2861 8.03027L9.56738 11.75L9.49219 11.8223C9.44184 11.8644 9.38625 11.8998 9.32715 11.9277L9.25879 11.9561L9.18848 11.9775C8.95022 12.0383 8.68101 11.9857 8.47559 11.7803L4.72559 8.03027L4.66602 7.96484C4.08284 7.26032 5.10054 6.28428 5.78613 6.96973L8.25586 9.43848V3.75L8.26074 3.66211C8.30423 3.28934 8.62143 3.00008 9.00586 3L9.09375 3.00488Z" fill="#1C1C1E"/>
                      </svg>
                    </template>
                  </AppButton>
                  <span v-else>-</span>
                </template>
                <template v-else-if="cell.column.id === 'uploadFile' || cell.column.id === 'templateFile'">
                  <a
                    v-if="cell.getValue()"
                    class="file-link"
                    target="_blank"
                    :class="{'downloadable': true}"
                    @click.prevent="handleFileClick(cell.row.original, cell.column.id)"
                  >
                    {{ cell.getValue() }}
                  </a>
                  <span v-else>-</span>
                </template>
                <template v-else>
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination flex items-center gap-2">
        <Pagination
          :pageCount="pageCount"
          :clickHandler="clickHandler"
          :value="pagination.pageIndex"
          :pageRange="5"
          firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { AppButton } from '@n11/so-ui-components';
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import Pagination from '@/components/Table/Pagination';
import { HISTORY_TAB_UPLOAD } from '@/utils/enum/bulkActionTabs';
import { SELLEROFFICE_API_BASE_URL, UMBRELLA_CAMPAIGNS_DOWNLOAD_TEMPLATE } from '@/utils/enum/endpoints';
import Request from '@/services/request';
import useJoinCampaignBulkService from '../services/joinCampaignBulkService';
import { getUploadHistoryColumns, getDownloadHistoryColumns } from '../services/getCampaignBulkHistoryColumns';
import { sendButtonClickEvent, sendTemplateDownloadFailEvent } from '@/utils/ga4/helper';
const Req = new Request();
const store = useStore();
const INITIAL_PAGE_INDEX = 0;
const INITIAL_PAGE_SIZE = 10;

const props = defineProps({
  refreshRef: {
    type: Object,
    default: () => ({
      refresh: false,
      tab: null,
      taskId: null,
      headerTab: ''
    })
  },
  selectedTabIndex: {
    type: String,
    required: true,
  },
  selectedHeaderTabIndex: {
    type: String,
    required: true
  },
  campaignId: {
    type: String,
    required: true
  },
  campaignName: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['tableStatus']);

const highlightedRows = ref([]);
const isEmptyTable = ref(false);
const campaignIdRef = ref(props.campaignId);
const pagination = ref({
  pageIndex: INITIAL_PAGE_INDEX,
  pageSize: INITIAL_PAGE_SIZE,
  type: props.selectedTabIndex,
  tab: props.selectedHeaderTabIndex,
  reload: false,
});
const tableColumns = ref(props.selectedTabIndex === HISTORY_TAB_UPLOAD ? getUploadHistoryColumns() : getDownloadHistoryColumns());
const { data, pageCount, isLoading } = useJoinCampaignBulkService(pagination, campaignIdRef);

const emptyText = computed(() => {
  if (props.selectedTabIndex === HISTORY_TAB_UPLOAD) {
    return {
      type: 'Henüz dosya yüklemediniz.',
      description: 'Dosya yüklediğinizde bu ekranda görebilirsiniz.'
    };
  }
  return {
    type: 'Henüz şablon indirmediniz.',
    description: 'Şablon indirdiğinizde bu ekranda görebilirsiniz.'
  };
});

const getTemplateDetailPagePath = () =>
  (typeof window !== 'undefined'
    ? (window.location.pathname.replace(/^\/selleroffice\/v2/, '') || '/').trim()
    : '');

const table = useVueTable({
  get data() {
    return data.value || [];
  },
  get columns() {
    return tableColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
  manualPagination: true,
  get pageCount() {
    return pageCount.value;
  },
  state: {
    get pagination() {
      return {
        pageIndex: pagination.value.pageIndex,
        pageSize: pagination.value.pageSize
      };
    },
  },
  meta: {
    getRowClasses: (row) => highlightedRows.value.includes(row.original.id) ? 'highlighted-row' : '',
  },
});

const clickHandler = (emittedPage) => {
  let targetPage = emittedPage;

  if (targetPage >= pageCount.value)
    targetPage = pageCount.value > 0 ? pageCount.value - 1 : 0;

  if (targetPage < 0) targetPage = 0;

  if (pagination.value.pageIndex !== targetPage) {
    pagination.value.pageIndex = targetPage;
    table.setPageIndex(targetPage);
  }
};

const downloadReport = async (row) => {
  try {
    const url = `/umbrella-campaigns/${props.campaignId}/bulk-products/upload/${row.id}/download`;
    const response = await Req.getFile(url, { downloadReport: true }, {
      baseURL: SELLEROFFICE_API_BASE_URL,
    });

    const blob = response.data;
    const fileName = row.reportFile || row.uploadFile || `report_${row.id}.xls`;

    const windowUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = windowUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(windowUrl);
  } catch (error) {
    console.error('Rapor indirilemedi:', error);
  }
};

const handleTemplateDownload = async (templateId, fileName, status) => {
  try {
    const response = await Req.getFile(
      UMBRELLA_CAMPAIGNS_DOWNLOAD_TEMPLATE.replace('{campaignId}', props.campaignId).replace('{templateId}', templateId),
      {},
      {
        baseURL: SELLEROFFICE_API_BASE_URL,
      }
    );
    
    const blob = response.data;
    const finalFileName = fileName || `campaign_template_${templateId}.xls`;

    const windowUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = windowUrl;
    link.setAttribute('download', finalFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(windowUrl);

    if (status === 'WAITING_FOR_DOWNLOAD') {
      sendButtonClickEvent({
        page_path: getTemplateDetailPagePath(),
        button_name: 'İndir',
        button_type: 'Download',
        campaign_id: props.campaignId,
        campaign_name: props.campaignName,
        page_type: 'Template Detail',
      });
    }

  } catch (error) {
    console.error('Şablon indirilemedi:', error);

    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Şablon indirilemedi.',
    });
    sendTemplateDownloadFailEvent({
      campaign_id: props.campaignId,
      campaign_name: props.campaignName,
      fail_reason: error?.data?.errors?.[0]?.message || 'Şablon indirilemedi',
    });
  }
};

const handleFileClick = async (row, columnId) => {
  try {
    if (columnId === 'templateFile') {
      await handleTemplateDownload(row.id, row.templateFile);
    } else if (columnId === 'uploadFile') {
      const hasError = row.failedCount > 0;
      
      const url = `/umbrella-campaigns/${props.campaignId}/bulk-products/upload/${row.id}/download`;

      const response = await Req.getFile(url, {
        downloadReport: hasError
      }, {
        baseURL: SELLEROFFICE_API_BASE_URL,
      });

      const blob = response.data;
      const fileName = hasError && row.reportFile ? row.reportFile : row.uploadFile;

      const windowUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = windowUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(windowUrl);
    }
  } catch (error) {
    console.error('Dosya indirilemedi:', error);
  }
};

// --- Watchers ---
watch(() => props.selectedTabIndex, (newTab) => {
  pagination.value.type = newTab;
  pagination.value.pageIndex = INITIAL_PAGE_INDEX;
  pagination.value.reload = true;
  tableColumns.value = newTab === HISTORY_TAB_UPLOAD ? getUploadHistoryColumns() : getDownloadHistoryColumns();
});

watch(() => props.refreshRef, (newRefresh) => {
  if (newRefresh?.refresh) {
    pagination.value.reload = true;
    if (newRefresh.taskId) {
      highlightedRows.value.push(newRefresh.taskId);
      setTimeout(() => {
        highlightedRows.value = highlightedRows.value.filter(id => id !== newRefresh.taskId);
      }, 3000);
    }
  }
}, { deep: true });

watch(data, (newData) => {
  isEmptyTable.value = !newData || newData.length === 0;
}, { immediate: true });

watch(isLoading, (newIsLoading) => {
  emit('tableStatus', { isLoading: newIsLoading });
});

watch(
  data,
  (newData) => {
    isEmptyTable.value = !newData || newData.length === 0;
  },
  { immediate: true },
);

watch(isLoading, (newIsLoading) => {
  emit('tableStatus', { isLoading: newIsLoading });
});

const hasRows = computed(() => table.getRowModel().rows.length > 0);

defineExpose({
  pageCount,
  pagination,
  clickHandler,
  hasRows,
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.p-2 {
  padding: 16px 24px;
}

.advanced-table-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  background: $colorN00;
  border-radius: 8px;

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    min-height: 400px;

    img {
      max-width: 120px;
    }
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    min-height: 400px;
    border-radius: 8px;
    border: 1px solid #B4B4B4;

    img {
      max-width: 120px;
      margin-bottom: 24px;
    }

    .empty-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;

      .empty-text-title {
        color: $colorN80;
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
      }

      .empty-text-description {
        color: $colorN80;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
      }
    }
  }

  .table-wrapper {
    overflow-x: auto;
    border: 1px solid $colorN50;
    border-radius: 8px;
  }

  .advanced-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    thead {
      background: $colorD10;

      tr {
        th {
          padding: 14px 12px;
          border-bottom: 2px solid $colorN30;
          border-right: 1px solid $colorN30;
          background-color: $colorBe30;
          color: $colorN80;
          font-size: 13px;
          font-weight: 600;
          text-align: left;

          &:last-child {
            border-right: none;
          }
        }
      }
    }

    tbody {
      tr {
        td {
          padding: 16px 12px;
          border-bottom: 1px solid $colorN30;
          border-right: 1px solid $colorN30;
          color: $colorN80;
          font-size: 13px;
          vertical-align: middle;
          height: 48px;
          box-sizing: border-box;

          &:last-child {
            border-right: none;
          }
        }

        &.highlighted-row {
          background-color: $colorM05;
        }
      }
    }
  }

  .error-column-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: $colorE50;
    gap: 2px;

    span {
      display: flex;
      align-items: center;
      font-size: 12px;
      gap: 4px;
      line-height: 1;
    }

    .report-button {
      display: flex;
      align-items: center;
      gap: 4px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      font-size: 12px;
      color: $colorM50;
    }
  }

  .file-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $colorN80;
    text-decoration: none;

    &.downloadable {
      color: $colorM50;
      cursor: pointer;
    }
  }

  .pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
